import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { SystemSettings } from '@/hooks/common';

interface DomainSettingsProps {
  settings: SystemSettings;
  onSettingsChange: (settings: Partial<SystemSettings>) => void;
  onSave: () => Promise<string>;
  loading: boolean;
}

export const DomainSettings: React.FC<DomainSettingsProps> = ({
  settings,
  onSettingsChange,
  onSave,
  loading,
}) => {
  const handleSave = async () => {
    try {
      await onSave();
    } catch (error) {
      // Error is handled by parent
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>도메인 설정</CardTitle>
        <CardDescription>배포/운영 중인 메인 도메인 주소와 서비스 경로를 설정합니다</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>서비스 URL 구성</Label>
            <div className="flex items-start gap-1 text-sm font-mono">
              <div className="flex flex-col items-center">
                <Select
                  value={settings.serviceProtocol}
                  onValueChange={(value) => onSettingsChange({ serviceProtocol: value })}
                >
                  <SelectTrigger className="h-10 px-2 text-sm font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="http">http://</SelectItem>
                    <SelectItem value="https">https://</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-400 mt-1">프로토콜</span>
              </div>
              <div className="flex flex-col items-center">
                <Input
                  id="main_domain"
                  value={settings.mainDomain}
                  onChange={(e) => onSettingsChange({ mainDomain: e.target.value })}
                  placeholder="도메인:포트"
                  className="w-40 font-mono text-sm h-10"
                />
                <span className="text-xs text-gray-400 mt-1">도메인(:포트)</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="h-10 flex items-center text-gray-500">/</div>
                <span className="text-xs text-gray-400 mt-1 invisible">-</span>
              </div>
              <div className="flex flex-col items-center">
                <Input
                  id="service_base_path"
                  value={settings.serviceBasePath.replace(/^\//, '')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\//, '');
                    onSettingsChange({ serviceBasePath: value ? `/${value}` : '' });
                  }}
                  placeholder=""
                  className="w-16 font-mono text-sm h-10"
                />
                <span className="text-xs text-gray-400 mt-1">기본경로</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="h-10 flex items-center text-gray-500">/services/</div>
                <span className="text-xs text-gray-400 mt-1">고정경로</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="h-10 flex items-center text-blue-600 italic">demo-ocr</div>
                <span className="text-xs text-gray-400 mt-1">서비스명</span>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              개발: http / localhost:8102 / (비워두기), 운영: https / gensapps.com / saas
            </p>
          </div>
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>서비스 URL 미리보기:</strong>{' '}
              <code className="bg-gray-200 px-1 rounded">
                {`${settings.serviceProtocol}://${settings.mainDomain}${settings.serviceBasePath}/services/demo-ocr`}
              </code>
            </p>
          </div>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? '저장 중...' : '저장'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

